import { getMessaging, getToken, Messaging, onMessage } from 'firebase/messaging';
import Dexie, { Table } from 'dexie';
import NotificationBase, {
  NotificationMessage,
  onClickNotificationHandler,
  onMessageHandler,
  TokenType,
} from './NotificationBase';
import { getFirebaseApp } from '../firebase';

export interface MessageCache {
  id: string;
  data: any;
  isSendbirdMessage: boolean;
}

export class PushClassedDexie extends Dexie {
  messageCache!: Table<MessageCache>;

  constructor() {
    super('webPushDatabase');
    this.version(1).stores({
      messageCache: 'id,data,isSendbirdMessage',
    });
  }
}

export const db = new PushClassedDexie();

const WEB_PUSH_VAPID_KEY =
  'BM-nZ084EXYnrlJSFQnUBSI3mkErdRx1qrSwVQvE0JjMPmelT6DwltT2YhwyvzwMyTFBXtPJ8r88CABWdHL-WgU';

const BACKGROUND_NOTIFICATION_HASH = 'msgKey';

function isFromAndroidApp() {
  const ref = document.referrer || '';
  return ref.startsWith('android-app://');
}

function hasTwaQueryFlag(flag = 'source', value = 'pwa') {
  const sp = new URLSearchParams(location.search);
  return sp.get(flag) === value;
}

const TWA_SESSION_FLAG = 'md_is_twa_session';

function getPersistedTwaFlag() {
  try {
    return sessionStorage.getItem(TWA_SESSION_FLAG) === '1';
  } catch {
    return false;
  }
}

function persistTwaFlag() {
  try {
    sessionStorage.setItem(TWA_SESSION_FLAG, '1');
  } catch {
    // noop
  }
}

export const isLikelyTWA = () => {
  // 0) 세션 플래그(리디렉트 왕복 등으로 referrer/쿼리가 유실되어도 유지)
  if (getPersistedTwaFlag()) return true;

  // 1) 확정적 신호: android-app:// referrer
  if (isFromAndroidApp()) {
    persistTwaFlag();
    return true;
  }

  // 2) 네이티브가 붙여준 플래그 (오타 포함 허용)
  if (hasTwaQueryFlag('source', 'pwa')) {
    persistTwaFlag();
    return true;
  }

  return false;
};

export class WebPushNotification extends NotificationBase {
  messaging: Messaging | null = null;
  private messageHandlers: Set<onMessageHandler> = new Set();
  private isMessageSubscribed = false;

  constructor() {
    super();
  }

  async lazyGetMessaging() {
    if (this.messaging) {
      return this.messaging;
    }
    const app = await getFirebaseApp();
    this.messaging = getMessaging(app);
    return this.messaging;
  }

  async getPermissionState() {
    return Notification.permission;
  }

  async requestPermission() {
    return Notification.requestPermission();
  }

  async requestToken(): Promise<TokenType | null> {
    // if (Notification.permission !== 'granted') {
    //   return null;
    // }
    try {
      const currentToken = await getToken(await this.lazyGetMessaging(), {
        vapidKey: WEB_PUSH_VAPID_KEY,
      });

      if (!currentToken) {
        return null;
      }

      return {
        platform: 'web',
        token: currentToken,
      };
    } catch (error) {
      console.log(error);
      return null;
    }
  }

  async onMessage(handler: onMessageHandler) {
    this.messageHandlers.add(handler);
    // console.log('[WebPushNotification] Handler added. Total handlers:', this.messageHandlers.size);

    // Firebase onMessage는 한 번만 구독하고, 모든 핸들러에 메시지 분배
    if (!this.isMessageSubscribed) {
      this.isMessageSubscribed = true;
      // console.log('[WebPushNotification] Subscribing to Firebase onMessage...');
      onMessage(await this.lazyGetMessaging(), (payload) => {
        // console.log('[WebPushNotification] Message received. Handlers count:', this.messageHandlers.size);
        const message = payload.data as NotificationMessage;
        this.messageHandlers.forEach((h) => {
          try {
            // console.log('[WebPushNotification] Calling handler...');
            h(message);
          } catch (e) {
            // console.error('Error in notification message handler:', e);
          }
        });
      });
    } else {
      // console.log('[WebPushNotification] Already subscribed, skipping Firebase subscription');
    }

    // 개별 핸들러 해제 함수 반환
    return () => {
      this.messageHandlers.delete(handler);
      // console.log('[WebPushNotification] Handler removed. Total handlers:', this.messageHandlers.size);
    };
  }

  setBadgeCount(count: number) {}

  onNotificationClick(handler: onClickNotificationHandler) {
    function notificationClickHandler(event: any) {
      const data = event?.data as NotificationMessage | undefined;
      if (!data) return;

      // navigate type 분기 처리
      if (data.type === 'navigate' && !!data.channelId) {
        handler({ ...data, channelId: data.channelId });
      }
    }
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('message', notificationClickHandler);
    }
    return () => {
      if ('serviceWorker' in navigator) {
        navigator.serviceWorker.removeEventListener('message', notificationClickHandler);
      }
    };
  }

  async clearNotificationsByChannel(channelId: string): Promise<void> {
    try {
      if (!('serviceWorker' in navigator)) return;

      // 1) Post message to all relevant service workers (firebase-messaging-sw.js)
      const regs = await navigator.serviceWorker.getRegistrations();
      const targetRegs = regs.filter(
        (r) =>
          r.active?.scriptURL.includes('firebase-messaging-sw.js') ||
          r.waiting?.scriptURL.includes('firebase-messaging-sw.js') ||
          r.installing?.scriptURL.includes('firebase-messaging-sw.js'),
      );
      for (const reg of targetRegs) {
        reg.active?.postMessage({ type: 'clear-notifications-by-channel', channelId });
      }

      // 2) Page-side fallback: attempt to close visible notifications from current registration
      const currentReg = await navigator.serviceWorker.getRegistration().catch(() => undefined);
      const notifications = await currentReg?.getNotifications?.({});
      notifications?.forEach((n) => {
        const nChannelId = (n.data && (n.data as any).channelId) || undefined;
        if (nChannelId === channelId) n.close();
      });
    } catch {
      // noop
    }
  }

  async clearAllNotifications(): Promise<void> {
    try {
      if (!('serviceWorker' in navigator)) return;
      // Close all notifications from all registrations in this origin
      const regs = await navigator.serviceWorker.getRegistrations();
      for (const reg of regs) {
        const notifications = await reg.getNotifications({});
        notifications.forEach((n) => n.close());
      }
    } catch {
      // noop
    }
  }

  async requestBackgroundPushNotificationMessage() {
    if (window.location.hash.includes(`#${BACKGROUND_NOTIFICATION_HASH}=`)) {
      const msgKey = window.location.hash.split(`#${BACKGROUND_NOTIFICATION_HASH}=`)[1];
      if (msgKey) {
        const messageCache = await db.messageCache.where({ id: msgKey }).first();
        if (messageCache) {
          window.dispatchEvent(new CustomEvent('push-notification-click', { detail: messageCache }));
        }
      }
    }
  }

  supportsNotifications(): boolean {
    return true;
  }

  setActiveChannel(channelId: string | null): void {
    // 순수 웹 구현에서는 네이티브에 전달할 대상이 없음 → no-op.
    // 포그라운드/백그라운드 필터링은 웹 SW 자체에서 navigator.serviceWorker
    // 클라이언트 상태를 보고 판단하거나, 별도 처리 불요.
    void channelId;
  }
}

export default WebPushNotification;
